import type {
  PublicationDetail,
  PublicationQueue,
  PublicationSummary,
} from "@/lib/types/api/agent-types";
import type {
  AgentInventoryPage,
  ForkEntry,
  SharedWorkflowDetail,
  DeploymentPreview,
  DeploymentRequest,
  DeploymentResult,
  RetirementPreview,
  RetirementResult,
} from "@/lib/types/api/agent-inventory-types";
import { BaseHttpClient } from "../../http/base-http-client";
import type {
  SystemStatusResponse,
  AdminDashboardSummaryResponse,
  AdminBulkOperationResponse,
  AdminGetUsersParams,
  AdminCreateUserRequest,
  AdminUpdateUserRequest,
  AdminBulkUpdateUsersRequest,
  AdminBulkMoveUsersRequest,
  AdminBulkImportUsersRequest,
  AdminBulkImportUsersResponse,
  AdminGetOrganizationsParams,
  AdminCreateOrganizationRequest,
  AdminUpdateOrganizationRequest,
  AdminGetAgentsParams,
  McpServerOut,
  McpServerCreate,
  McpServerUpdate,
  McpServerTestRequest,
  McpServerTestResponse,
  OrganizationAnalyticsResponse,
  AgentStatsResponse,
  DefaultAgentInfoResponse,
  OrganizationDeleteResponse,
  AgentRagConfig,
  AgentRagConfigCreate,
  AgentRagConfigUpdate,
  AgentRagConfigTestRequest,
  AgentRagConfigTestResponse,
  McpToolOut,
  McpToolCreate,
  McpToolUpdate,
  McpToolSchemaOut,
  McpToolTestRequest,
  McpToolInvokeRequest,
  PlatformModel,
  PlatformModelListParams,
  PlatformModelListResponse,
  PlatformModelCreateRequest,
  PlatformModelUpdateRequest,
  PlatformModelTestResult,
  SelectableModel,
} from "../../types/api/admin-types";
import type {
  OrganizationWithAdminCreateRequest,
  OrganizationWithAdminCreateResponse,
} from "../../types/api/user-types";
import type { User, Organization } from "../../types/api/user-types";
import type { AgentWithOrganization } from "../../types/api/agent-types";
import type { PaginatedResponse } from "../../types/api/common-types";

/**
 * Admin API Client
 *
 * Handles all admin-related endpoints:
 * - User management (CRUD, bulk operations, role management)
 * - Organization management (CRUD, analytics)
 * - Agent management (listing, stats)
 * - System status and dashboard operations
 *
 * Follows SRP: Single responsibility for admin operations
 * Follows SOC: Separated from business logic and UI concerns
 */
export class AdminApiClient extends BaseHttpClient {
  // ==================== USER MANAGEMENT ====================

  /**
   * Get list of users with optional filtering and pagination
   * @param params - Optional filtering and pagination parameters
   * @returns Promise<PaginatedResponse<User>> - Paginated list of users
   */
  async getUsers(
    params?: AdminGetUsersParams
  ): Promise<PaginatedResponse<User>> {
    // Only send skip and limit parameters that the backend expects
    const queryParams: Partial<AdminGetUsersParams> = {};
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;

    return this.get<PaginatedResponse<User>>("admin/users", queryParams);
  }

  /**
   * Create a new user account
   * @param data - User creation data including email, name, and role settings
   * @returns Promise<User> - Created user information
   */
  async createUser(data: AdminCreateUserRequest): Promise<User> {
    return this.post<User>("admin/users", data);
  }

  /**
   * Update an existing user's information
   * @param userId - The user ID to update
   * @param data - Partial user data to update
   * @returns Promise<User> - Updated user information
   */
  async updateUser(
    userId: string,
    data: AdminUpdateUserRequest
  ): Promise<User> {
    return this.put<User>(`admin/users/${userId}`, data);
  }

  /**
   * Delete a user account
   * @param userId - The user ID to delete
   * @returns Promise<void>
   */
  async deleteUser(userId: string): Promise<void> {
    return this.delete(`admin/users/${userId}`);
  }

  /**
   * Toggle organization admin role for a user
   * @param userId - The user ID to toggle role for
   * @returns Promise<User> - Updated user with new role
   */
  async toggleUserOrgAdmin(userId: string): Promise<User> {
    return this.post<User>(`admin/users/${userId}/toggle-org-admin`, {});
  }

  /**
   * Toggle superuser role for a user
   * @param userId - The user ID to toggle superuser role for
   * @returns Promise<User> - Updated user with new role
   */
  async toggleUserSuperuser(userId: string): Promise<User> {
    return this.post<User>(`admin/users/${userId}/toggle-superuser`, {});
  }

  // ==================== BULK USER OPERATIONS ====================

  /**
   * Update multiple users at once
   * @param data - Bulk update request with user IDs and update data
   * @returns Promise<AdminBulkOperationResponse> - Operation results and any errors
   */
  async bulkUpdateUsers(
    data: AdminBulkUpdateUsersRequest
  ): Promise<AdminBulkOperationResponse> {
    return this.patch<AdminBulkOperationResponse>(
      "/admin/users/bulk-update",
      data
    );
  }

  /**
   * Delete multiple users at once
   * @param userIds - Array of user IDs to delete
   * @returns Promise<AdminBulkOperationResponse> - Operation results and any errors
   */
  async bulkDeleteUsers(
    userIds: string[]
  ): Promise<AdminBulkOperationResponse> {
    return this.delete<AdminBulkOperationResponse>("admin/users/bulk-delete", {
      data: userIds,
    });
  }

  /**
   * Move multiple users to a different organization
   * @param data - Bulk move request with user IDs and target organization
   * @returns Promise<AdminBulkOperationResponse> - Operation results and any errors
   */
  async bulkMoveUsersToOrganization(
    data: AdminBulkMoveUsersRequest
  ): Promise<AdminBulkOperationResponse> {
    return this.patch<AdminBulkOperationResponse>(
      "/admin/users/bulk-move-organization",
      data
    );
  }

  /**
   * Create multiple users at once
   * @param users - Array of user creation data
   * @returns Promise<User[]> - Array of created users
   */
  async bulkCreateUsers(users: AdminCreateUserRequest[]): Promise<User[]> {
    return this.post<User[]>("admin/users/bulk-create", users);
  }

  /**
   * Bulk import users from CSV with invitation emails
   * @param data - Bulk import request with user data and options
   * @returns Promise<BulkUserImportResponse> - Import results with success/error details
   */
  async bulkImportUsers(
    data: AdminBulkImportUsersRequest
  ): Promise<AdminBulkImportUsersResponse> {
    return this.post<AdminBulkImportUsersResponse>(
      "/admin/users/bulk-import",
      data
    );
  }

  // ==================== ORGANIZATION MANAGEMENT ====================

  /**
   * Get list of organizations with optional filtering and pagination
   * @param params - Optional filtering and pagination parameters
   * @returns Promise<PaginatedResponse<Organization>> - Paginated list of organizations
   */
  async getOrganizations(
    params?: AdminGetOrganizationsParams
  ): Promise<PaginatedResponse<Organization>> {
    // Only send skip and limit parameters that the backend expects
    const queryParams: Partial<AdminGetOrganizationsParams> = {};
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;

    try {
      // Try superadmin endpoint first (returns array, not paginated)
      const organizationsArray = await this.get<Organization[]>(
        "/admin/organizations",
        queryParams
      );

      // Wrap array response in paginated format for consistency
      const skip = queryParams.skip ?? 0;
      const limit = queryParams.limit ?? 100;
      return {
        items: organizationsArray,
        total: organizationsArray.length,
        limit,
        offset: skip,
        has_more: false, // Admin endpoint returns all matching results
      } as PaginatedResponse<Organization>;
    } catch (error: any) {
      // Fallback for organization admins: use general organizations listing (returns paginated)
      const status = error?.response?.status;
      if (status === 403) {
        try {
          return await this.get<PaginatedResponse<Organization>>(
            "/organizations/",
            queryParams
          );
        } catch (fallbackError: any) {
          // If general orgs endpoint is unavailable or returns 404, provide an empty list
          const fallbackStatus = fallbackError?.response?.status;
          if (fallbackStatus === 404) {
            const skip = queryParams.skip ?? 0;
            const limit = queryParams.limit ?? 100;
            return {
              items: [],
              total: 0,
              limit,
              offset: skip,
              has_more: false,
            } as PaginatedResponse<Organization>;
          }
          throw fallbackError;
        }
      }
      throw error;
    }
  }

  /**
   * Get analytics data for a specific organization
   * @param orgId - The organization ID to get analytics for
   * @returns Promise<OrganizationAnalyticsResponse> - Organization analytics data
   */
  async getOrganizationAnalytics(
    orgId: string
  ): Promise<OrganizationAnalyticsResponse> {
    return this.get<OrganizationAnalyticsResponse>(
      `/admin/organizations/${orgId}/analytics`
    );
  }

  /**
   * Create a new organization
   * @param data - Organization creation data
   * @returns Promise<Organization> - Created organization information
   */
  async createOrganization(
    data: AdminCreateOrganizationRequest
  ): Promise<Organization> {
    return this.post<Organization>("admin/organizations", data);
  }

  /**
   * Create an organization with an admin user
   * @param data - Organization and admin user creation data
   * @returns Promise<OrganizationWithAdminCreateResponse> - Created organization and admin user
   */
  async createOrganizationWithAdmin(
    data: OrganizationWithAdminCreateRequest
  ): Promise<OrganizationWithAdminCreateResponse> {
    return this.post<OrganizationWithAdminCreateResponse>(
      "/admin/organizations/with-admin",
      data
    );
  }

  /**
   * Update an existing organization
   * @param organizationId - The organization ID to update
   * @param data - Organization update data
   * @returns Promise<Organization> - Updated organization information
   */
  async updateOrganization(
    organizationId: string,
    data: AdminUpdateOrganizationRequest
  ): Promise<Organization> {
    return this.put<Organization>(
      `/admin/organizations/${organizationId}`,
      data
    );
  }

  /**
   * Delete an organization and all associated data
   * @param organizationId - The organization ID to delete
   * @returns Promise<OrganizationDeleteResponse> - Deletion results with counts
   */
  async deleteOrganization(
    organizationId: string
  ): Promise<OrganizationDeleteResponse> {
    return this.delete<OrganizationDeleteResponse>(
      `/admin/organizations/${organizationId}`
    );
  }

  // ==================== AGENT MANAGEMENT ====================

  /**
   * Get list of agents with optional filtering and pagination
   * @param params - Optional filtering and pagination parameters
   * @returns Promise<AgentWithOrganization[]> - Array of agents with organization info
   */
  async getAgents(
    params?: AdminGetAgentsParams
  ): Promise<AgentWithOrganization[]> {
    // Only send skip and limit parameters that the backend expects
    const queryParams: Partial<AdminGetAgentsParams> = {};
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;

    // Backend returns an array of AgentWithOrganization, not paginated
    return this.get<AgentWithOrganization[]>("admin/agents", queryParams);
  }

  /**
   * Get detailed statistics for a specific agent
   * @param agentId - The agent ID to get stats for
   * @returns Promise<AgentStatsResponse> - Agent statistics including usage data
   */
  // ==================== AGENT INVENTORY (governance) ====================

  /**
   * The organisation's Agents and exact published releases — the bounded
   * projection behind Admin → Agents. Identity, state, usage and audience
   * size; deliberately no Agent internals.
   */
  async getAgentInventory(
    page: number = 1,
    pageSize: number = 10
  ): Promise<AgentInventoryPage> {
    return this.get<AgentInventoryPage>("admin/agent-inventory", {
      page,
      page_size: pageSize,
    });
  }

  /** One Agent's shared Workflows and who installed each. Fetched when the
   * panel opens: the consumer list is unbounded and only one card's is ever
   * wanted. */
  async getSharedWorkflows(agentId: string): Promise<SharedWorkflowDetail[]> {
    return this.get<SharedWorkflowDetail[]>(
      `admin/agent-inventory/agents/${encodeURIComponent(agentId)}/shared-workflows`
    );
  }

  /** The Agents forked from THIS exact Agent or release. */
  async getAgentForks(agentId: string): Promise<ForkEntry[]> {
    return this.get<ForkEntry[]>(
      `admin/agent-inventory/agents/${encodeURIComponent(agentId)}/forks`
    );
  }

  /** What deploying would do. Informational — execution recomputes. */
  async previewChatDeployment(
    body: DeploymentRequest
  ): Promise<DeploymentPreview> {
    return this.post<DeploymentPreview>(
      "admin/agent-inventory/deployments/preview",
      body
    );
  }

  /**
   * Copy a release's Chat audience onto a newer one. Only the two release ids
   * are sent: the audience is derived server-side, never named by the browser.
   */
  async executeChatDeployment(
    body: DeploymentRequest
  ): Promise<DeploymentResult> {
    return this.post<DeploymentResult>(
      "admin/agent-inventory/deployments",
      body
    );
  }

  /** What retiring this exact release's audience would cost. */
  async previewReleaseRetirement(
    releaseId: string
  ): Promise<RetirementPreview> {
    return this.post<RetirementPreview>(
      `admin/agent-inventory/releases/${releaseId}/retirement/preview`,
      {}
    );
  }

  /** Revoke this exact release's Human Chat audience. Nothing else. */
  async executeReleaseRetirement(releaseId: string): Promise<RetirementResult> {
    return this.post<RetirementResult>(
      `admin/agent-inventory/releases/${releaseId}/retirement`,
      {}
    );
  }

  async getAgentStats(agentId: string): Promise<AgentStatsResponse> {
    return this.get<AgentStatsResponse>(`admin/agents/${agentId}/stats`);
  }

  /**
   * Permanently delete an agent (admin authority). The backend runs the
   * full owner-path cleanup (RAG corpus + cascading deletion), works for
   * orphaned agents whose owner account no longer exists, and refuses the
   * platform default agent.
   */
  async deleteAgent(agentId: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`admin/agents/${agentId}`);
  }

  // ==================== SYSTEM MANAGEMENT ====================

  /**
   * Get system health status
   * @returns Promise<SystemStatusResponse> - System health information
   */
  async getSystemStatus(): Promise<SystemStatusResponse> {
    return this.get<SystemStatusResponse>("admin/system-status");
  }

  /**
   * Get admin dashboard summary data
   * @returns Promise<AdminDashboardSummaryResponse> - Dashboard summary for current admin
   */
  async getDashboardSummary(): Promise<AdminDashboardSummaryResponse> {
    return this.get<AdminDashboardSummaryResponse>("admin/dashboard/summary");
  }

  /**
   * Get information about the default agent
   * @returns Promise<DefaultAgentInfoResponse> - Default agent information
   */
  async getDefaultAgentInfo(): Promise<DefaultAgentInfoResponse> {
    return this.get<DefaultAgentInfoResponse>("admin/default-agent-info");
  }

  // ==================== RAG CONFIG MANAGEMENT ====================

  /**
   * Get RAG configuration for a specific agent
   * @param agentId - The agent ID to get RAG config for
   * @returns Promise<AgentRagConfig> - Agent RAG configuration
   */
  async getAgentRagConfig(agentId: string): Promise<AgentRagConfig> {
    return this.get<AgentRagConfig>(`agents/${agentId}/rag-config`);
  }

  // ==================== MCP TOOLS (Per-Agent) ====================

  /**
   * List MCP tools for an agent
   */
  async listMcpTools(
    agentId: string,
    params?: { enabled?: boolean; name?: string; skip?: number; limit?: number }
  ): Promise<McpToolOut[]> {
    const queryParams: Record<string, unknown> = {};
    if (params?.enabled !== undefined) queryParams.enabled = params.enabled;
    if (params?.name !== undefined) queryParams.name = params.name;
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    return this.get<McpToolOut[]>(`agents/${agentId}/mcp-tools`, queryParams);
  }

  /**
   * Create MCP tool for an agent
   */
  async createMcpTool(
    agentId: string,
    data: McpToolCreate
  ): Promise<McpToolOut> {
    return this.post<McpToolOut>(`agents/${agentId}/mcp-tools`, data);
  }

  /**
   * Get MCP tool by ID
   */
  async getMcpTool(agentId: string, toolId: string): Promise<McpToolOut> {
    return this.get<McpToolOut>(`agents/${agentId}/mcp-tools/${toolId}`);
  }

  /**
   * Update MCP tool by ID
   */
  async updateMcpTool(
    agentId: string,
    toolId: string,
    data: McpToolUpdate
  ): Promise<McpToolOut> {
    return this.put<McpToolOut>(`agents/${agentId}/mcp-tools/${toolId}`, data);
  }

  /**
   * Delete MCP tool by ID
   */
  async deleteMcpTool(agentId: string, toolId: string): Promise<void> {
    return this.delete<void>(`agents/${agentId}/mcp-tools/${toolId}`);
  }

  /**
   * Enable an MCP tool
   */
  async enableMcpTool(agentId: string, toolId: string): Promise<McpToolOut> {
    return this.post<McpToolOut>(
      `/agents/${agentId}/mcp-tools/${toolId}/enable`,
      {}
    );
  }

  /**
   * Disable an MCP tool
   */
  async disableMcpTool(agentId: string, toolId: string): Promise<McpToolOut> {
    return this.post<McpToolOut>(
      `/agents/${agentId}/mcp-tools/${toolId}/disable`,
      {}
    );
  }

  /**
   * Sync MCP tool schema and return latest
   */
  async syncMcpToolSchema(
    agentId: string,
    toolId: string
  ): Promise<McpToolSchemaOut> {
    return this.post<McpToolSchemaOut>(
      `/agents/${agentId}/mcp-tools/${toolId}/sync-schema`,
      {}
    );
  }

  /**
   * Get latest MCP tool schema
   */
  async getMcpToolSchema(
    agentId: string,
    toolId: string
  ): Promise<McpToolSchemaOut> {
    return this.get<McpToolSchemaOut>(
      `/agents/${agentId}/mcp-tools/${toolId}/schema`
    );
  }

  /**
   * Test MCP tool with sample input
   */
  async testMcpTool(
    agentId: string,
    toolId: string,
    data: McpToolTestRequest
  ): Promise<{ output: unknown }> {
    return this.post<{ output: unknown }>(
      `/agents/${agentId}/mcp-tools/${toolId}/test`,
      data
    );
  }

  /**
   * Proxy invoke MCP tool
   */
  async proxyInvokeMcpTool(
    agentId: string,
    toolId: string,
    data: McpToolInvokeRequest
  ): Promise<{ output: unknown }> {
    return this.post<{ output: unknown }>(
      `/agents/${agentId}/mcp-tools/${toolId}/invoke`,
      data
    );
  }

  // ==================== MCP SERVERS (Per-Agent) ====================

  async listMcpServers(
    agentId: string,
    params?: { enabled?: boolean; name?: string; skip?: number; limit?: number }
  ): Promise<McpServerOut[]> {
    const queryParams: Record<string, unknown> = {};
    if (params?.enabled !== undefined) queryParams.enabled = params.enabled;
    if (params?.name !== undefined) queryParams.name = params.name;
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    return this.get<McpServerOut[]>(
      `/agents/${agentId}/mcp-servers`,
      queryParams
    );
  }

  async createMcpServer(
    agentId: string,
    data: McpServerCreate
  ): Promise<McpServerOut> {
    return this.post<McpServerOut>(`agents/${agentId}/mcp-servers`, data);
  }

  async getMcpServer(agentId: string, serverId: string): Promise<McpServerOut> {
    return this.get<McpServerOut>(`agents/${agentId}/mcp-servers/${serverId}`);
  }

  async updateMcpServer(
    agentId: string,
    serverId: string,
    data: McpServerUpdate
  ): Promise<McpServerOut> {
    return this.put<McpServerOut>(
      `/agents/${agentId}/mcp-servers/${serverId}`,
      data
    );
  }

  async deleteMcpServer(agentId: string, serverId: string): Promise<void> {
    return this.delete<void>(`agents/${agentId}/mcp-servers/${serverId}`);
  }

  async enableMcpServer(
    agentId: string,
    serverId: string
  ): Promise<McpServerOut> {
    return this.post<McpServerOut>(
      `/agents/${agentId}/mcp-servers/${serverId}/enable`,
      {}
    );
  }

  async disableMcpServer(
    agentId: string,
    serverId: string
  ): Promise<McpServerOut> {
    return this.post<McpServerOut>(
      `/agents/${agentId}/mcp-servers/${serverId}/disable`,
      {}
    );
  }

  async testMcpServer(
    agentId: string,
    serverId: string,
    data: McpServerTestRequest
  ): Promise<McpServerTestResponse> {
    return this.post<McpServerTestResponse>(
      `/agents/${agentId}/mcp-servers/${serverId}/test`,
      data
    );
  }

  /**
   * Create RAG configuration for an agent
   * @param agentId - The agent ID to create RAG config for
   * @param data - RAG configuration data
   * @returns Promise<AgentRagConfig> - Created RAG configuration
   */
  async createAgentRagConfig(
    agentId: string,
    data: AgentRagConfigCreate
  ): Promise<AgentRagConfig> {
    return this.post<AgentRagConfig>(`agents/${agentId}/rag-config`, data);
  }

  /**
   * Update RAG configuration for an agent
   * @param agentId - The agent ID to update RAG config for
   * @param data - Updated RAG configuration data
   * @returns Promise<AgentRagConfig> - Updated RAG configuration
   */
  async updateAgentRagConfig(
    agentId: string,
    data: AgentRagConfigUpdate
  ): Promise<AgentRagConfig> {
    return this.put<AgentRagConfig>(`agents/${agentId}/rag-config`, data);
  }

  /**
   * Delete RAG configuration for an agent
   * @param agentId - The agent ID to delete RAG config for
   * @returns Promise<{ message: string }> - Deletion confirmation
   */
  async deleteAgentRagConfig(agentId: string): Promise<{ message: string }> {
    return this.delete<{ message: string }>(`agents/${agentId}/rag-config`);
  }

  /**
   * Test RAG configuration connectivity for an agent
   * @param agentId - The agent ID to test RAG config for
   * @param data - Test configuration options
   * @returns Promise<AgentRagConfigTestResponse> - Test results
   */
  async testAgentRagConfig(
    agentId: string,
    data: AgentRagConfigTestRequest
  ): Promise<AgentRagConfigTestResponse> {
    return this.post<AgentRagConfigTestResponse>(
      `/agents/${agentId}/rag-config/test`,
      data
    );
  }

  // ==================== PLATFORM MODEL REGISTRY ====================

  /** List registry models with search/filters, counts, and summary. */
  async getPlatformModels(
    params?: PlatformModelListParams
  ): Promise<PlatformModelListResponse> {
    return this.get<PlatformModelListResponse>("admin/models", params);
  }

  /** Get one registry model with usage counts. */
  async getPlatformModel(modelId: string): Promise<PlatformModel> {
    return this.get<PlatformModel>(`admin/models/${modelId}`);
  }

  /** Create an admin-managed model (key immutable after creation). */
  async createPlatformModel(
    data: PlatformModelCreateRequest
  ): Promise<PlatformModel> {
    return this.post<PlatformModel>("admin/models", data);
  }

  /** Edit an admin-managed model (deployment rows are read-only). */
  async updatePlatformModel(
    modelId: string,
    data: PlatformModelUpdateRequest
  ): Promise<PlatformModel> {
    return this.patch<PlatformModel>(`admin/models/${modelId}`, data);
  }

  /** Enable an admin-managed model. */
  async enablePlatformModel(modelId: string): Promise<PlatformModel> {
    return this.post<PlatformModel>(`admin/models/${modelId}/enable`, {});
  }

  /** Disable an admin-managed model (references are never rewritten). */
  async disablePlatformModel(modelId: string): Promise<PlatformModel> {
    return this.post<PlatformModel>(`admin/models/${modelId}/disable`, {});
  }

  /** Run the live model test through the production provider adapter. */
  async testPlatformModel(modelId: string): Promise<PlatformModelTestResult> {
    return this.post<PlatformModelTestResult>(
      `admin/models/${modelId}/test`,
      {}
    );
  }

  /** Registry-backed selectable models for new configurations (any user). */
  async getSelectableModels(modelType?: string): Promise<SelectableModel[]> {
    return this.get<SelectableModel[]>(
      "models/selectable",
      modelType ? { model_type: modelType } : undefined
    );
  }

  // --- Organisation publication administration (Phase O) ------------------ //
  // Authorization is ORGANISATION_MANAGE_GRANTS + org residency — never any
  // Agent capability; every field comes from the frozen candidate package.

  async getPublicationsSummary(): Promise<PublicationSummary> {
    return this.get<PublicationSummary>("organisation/publications/summary");
  }

  async listPublications(): Promise<PublicationQueue> {
    return this.get<PublicationQueue>("organisation/publications");
  }

  async getPublication(candidateId: string): Promise<PublicationDetail> {
    return this.get<PublicationDetail>(
      `organisation/publications/${candidateId}`
    );
  }

  async rejectPublication(
    candidateId: string,
    reason: string | null
  ): Promise<PublicationDetail> {
    return this.post<PublicationDetail>(
      `organisation/publications/${candidateId}/reject`,
      { reason }
    );
  }

  /**
   * Save (and optionally finalize) the PRODUCTION decision — the grants lane.
   *
   * Finalizing records this Human as the reviewer of record; they remain the
   * grantor of the production authority a later promotion issues, whoever
   * presses Publish.
   */
  async saveResourceReview(
    candidateId: string,
    resourceMappings: Record<string, string>,
    modelBindingOverride: string | null,
    finalize: boolean
  ): Promise<PublicationDetail> {
    return this.put<PublicationDetail>(
      `organisation/publications/${candidateId}/resource-review`,
      {
        resource_mappings: resourceMappings,
        model_binding_override: modelBindingOverride,
        finalize,
      }
    );
  }

  /**
   * Save (and optionally finalize) ONE Workflow's compliance assessment —
   * the compliance lane. A prefilled assessment is not assessed until it is
   * finalized here by a Human.
   */
  async saveWorkflowAssessment(
    candidateId: string,
    sourceWorkflowId: string,
    body: {
      classification: string;
      intended_purpose: string | null;
      assessment: string | null;
      compliance_guidance: string | null;
      article_6_3_basis: string[];
      finalize: boolean;
    }
  ): Promise<PublicationDetail> {
    return this.put<PublicationDetail>(
      `organisation/publications/${candidateId}/compliance-assessments/${sourceWorkflowId}`,
      body
    );
  }

  /**
   * Publish. Carries NO review payload: the Backend consumes the frozen lane
   * decisions, so a compliance-only finalizer can promote a resource-reviewed
   * candidate without ever receiving the mappings.
   */
  async promotePublication(
    candidateId: string
  ): Promise<{ release_agent_id: string; release_number: number }> {
    return this.post(`organisation/publications/${candidateId}/promote`, {});
  }
}

/**
 * Create a default AdminApiClient instance
 * @returns AdminApiClient - Configured admin API client
 */
export function createAdminApiClient(): AdminApiClient {
  return new AdminApiClient();
}

/**
 * Default AdminApiClient instance for immediate use
 */
export const adminApiClient = createAdminApiClient();

/**
 * Admin dashboard rating (Dashboard Cost-1).
 *
 * The backend returns EVIDENCE — quantities and the applicable price sheet's
 * rates as exact decimal strings — and never a monetary total. Money is
 * computed in `lib/admin/rating.ts`, in exact decimal, from what these
 * return.
 */
export interface AdminTodayActivity {
  date: string;
  timezone: string;
  messages: number;
  active_agents: number;
}

/**
 * The viewer's IANA zone, straight from the browser.
 *
 * "Today" is a human question, so the answer has to come from the human's
 * calendar rather than the server's. Sent as a query parameter and used for
 * grouping only — nothing about it is persisted, and every timestamp on the
 * wire stays UTC.
 */
export function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    // Environments without a resolvable zone fall back to the backend's
    // documented default rather than to a guess made here.
    return undefined;
  }
}

export class AdminRatingApiClient extends BaseHttpClient {
  /** Per-day rating evidence over a fixed recent window. */
  getRatingSummary(days = 30, timeZone?: string): Promise<unknown> {
    const zone = timeZone ? `&timezone=${encodeURIComponent(timeZone)}` : "";
    return this.get<unknown>(`admin/usage/rating-summary?days=${days}${zone}`);
  }

  /** Same days/timezone contract as the rating summary, deliberately: the
   * leaders and the consumption chart must describe ONE period. */
  getUsageLeaders(days = 30, timeZone?: string): Promise<UsageLeaders> {
    const zone = timeZone ? `&timezone=${encodeURIComponent(timeZone)}` : "";
    return this.get<UsageLeaders>(`admin/usage/leaders?days=${days}${zone}`);
  }

  getTodayActivity(timeZone?: string): Promise<AdminTodayActivity> {
    const zone = timeZone ? `?timezone=${encodeURIComponent(timeZone)}` : "";
    return this.get<AdminTodayActivity>(`admin/usage/today-activity${zone}`);
  }
}

export const adminRatingApiClient = new AdminRatingApiClient();

/**
 * The dashboard's two consumption leaders (Admin usage).
 *
 * Shaped so the browser can reuse `lib/admin/rating.ts` unchanged: each
 * leader's usage is the SAME structure a `RatingDay` carries, so tokens go
 * through `summariseTokens` and money through `summariseCost` — no second
 * arithmetic, and no money computed on the server.
 */
export interface UsageLeaderUsage {
  input_tokens: string;
  cached_input_tokens: string;
  output_tokens: string;
  invocations: number;
  unmetered_invocations: number;
  model_invocations: number;
  rateable_invocations: number;
  price_buckets: RatingBucketWire[];
  unrateable: { count: number; reasons: Record<string, number> };
}

/** A price bucket exactly as the rating surface already spells it. */
export interface RatingBucketWire {
  pricing_revision_id: string;
  currency: string;
  unit_quantity: string;
  input_price: string | null;
  cached_input_price: string | null;
  output_price: string | null;
  input_tokens: string;
  cached_input_tokens: string;
  output_tokens: string;
  invocations: number;
}

export interface HumanLeader extends UsageLeaderUsage {
  principal_id: string;
  principal_type: string | null;
  display_name: string | null;
  bases: Record<string, number>;
}

export interface AgentLeader extends UsageLeaderUsage {
  agent_id: string;
  display_name: string | null;
}

export interface UsageLeaders {
  period: { start: string; end: string };
  /** Null when no eligible measured human usage — never a zero leader. */
  human: HumanLeader | null;
  agent: AgentLeader | null;
}
